<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div class="row justify-content-between align-items-center mb-4">
        <div class="col-md-12">
          <div class="d-flex justify-content-between align-items-center dq-page-actions">
            <h6 class="fw-medium d-inline-flex align-items-center mb-3 mb-sm-0">
              <router-link :to="{ name: 'crm.clients.list' }">
                <i class="ti ti-arrow-left me-2"></i>Clients
              </router-link>
              <span class="text-gray d-inline-flex ms-2">/ {{ clientName }}</span>
            </h6>

            <div class="d-flex align-items-center">
              <button
                type="button"
                class="btn btn-sm btn-white me-2"
                :disabled="loading"
                @click="reload"
              >
                <i class="ti ti-refresh me-1"></i>Reload
              </button>

              <div class="ms-2 head-icons">
                <a
                  href="javascript:void(0);"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-original-title="Collapse"
                  id="collapse-header"
                  @click="toggleHeader"
                >
                  <i class="ti ti-chevrons-up"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <div class="row">
        <div class="col-xl-4 theiaStickySidebar">
          <div class="card card-bg-1">
            <div class="card-body p-0">
              <span class="avatar avatar-xl avatar-rounded border border-2 border-white m-auto d-flex mb-2">
                <img :src="clientLogo" class="w-auto h-auto" alt="Logo" />
              </span>

              <div class="text-center px-3 pb-3 border-bottom">
                <h5 class="d-flex align-items-center justify-content-center mb-1">
                  {{ clientName }}
                  <i
                    v-if="isActive"
                    class="ti ti-discount-check-filled text-success ms-1"
                  ></i>
                </h5>

                <p class="text-dark mb-1">{{ clientEmail || "—" }}</p>
                <span :class="['badge', isActive ? 'badge-soft-success' : 'badge-soft-danger']">
                  {{ clientStatus }}
                </span>

                <div class="row gx-2 mt-3">
                  <div class="col-6">
                    <a
                      v-if="clientPhone"
                      :href="`tel:${clientPhone}`"
                      class="btn btn-dark w-100"
                    >
                      <i class="ti ti-phone-call me-1"></i>Call
                    </a>
                    <button v-else type="button" class="btn btn-dark w-100" disabled>
                      <i class="ti ti-phone-call me-1"></i>Call
                    </button>
                  </div>
                  <div class="col-6">
                    <a
                      v-if="clientEmail"
                      :href="`mailto:${clientEmail}`"
                      class="btn btn-primary w-100"
                    >
                      <i class="ti ti-mail me-1"></i>Email
                    </a>
                    <button v-else type="button" class="btn btn-primary w-100" disabled>
                      <i class="ti ti-mail me-1"></i>Email
                    </button>
                  </div>
                </div>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Account Basics</h6>

                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="d-inline-flex align-items-center">
                    <i class="ti ti-id me-2"></i>Account ID
                  </span>
                  <p class="text-dark mb-0">{{ clientCode }}</p>
                </div>

                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="d-inline-flex align-items-center">
                    <i class="ti ti-phone me-2"></i>Phone
                  </span>
                  <p class="text-dark mb-0">{{ clientPhone || "—" }}</p>
                </div>

                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="d-inline-flex align-items-center">
                    <i class="ti ti-world-www me-2"></i>Website
                  </span>
                  <a
                    v-if="clientWebsite"
                    :href="clientWebsite"
                    target="_blank"
                    rel="noopener"
                    class="text-info"
                  >
                    Visit
                  </a>
                  <span v-else class="text-dark">—</span>
                </div>

                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="d-inline-flex align-items-center">
                    <i class="ti ti-map-pin me-2"></i>Address
                  </span>
                  <p class="text-dark text-end mb-0">{{ clientAddress || "—" }}</p>
                </div>

                <div class="d-flex align-items-center justify-content-between">
                  <span class="d-inline-flex align-items-center">
                    <i class="ti ti-calendar-check me-2"></i>Added On
                  </span>
                  <p class="text-dark mb-0">{{ formatDate(clientCreatedAt) }}</p>
                </div>
              </div>

              <div class="p-3">
                <h6 class="mb-2">Relationship Owner</h6>
                <p class="text-dark mb-1">{{ ownerName }}</p>
                <p class="text-muted mb-0">{{ ownerEmail }}</p>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-6 d-flex">
              <div class="card flex-fill">
                <div class="card-body">
                  <p class="fs-12 text-gray-5 mb-1">Contacts</p>
                  <h4>{{ contacts.length }}</h4>
                </div>
              </div>
            </div>
            <div class="col-6 d-flex">
              <div class="card flex-fill">
                <div class="card-body">
                  <p class="fs-12 text-gray-5 mb-1">Deals</p>
                  <h4>{{ deals.length }}</h4>
                </div>
              </div>
            </div>
            <div class="col-6 d-flex">
              <div class="card flex-fill">
                <div class="card-body">
                  <p class="fs-12 text-gray-5 mb-1">Projects</p>
                  <h4>{{ projects.length }}</h4>
                </div>
              </div>
            </div>
            <div class="col-6 d-flex">
              <div class="card flex-fill">
                <div class="card-body">
                  <p class="fs-12 text-gray-5 mb-1">Open Pipeline</p>
                  <h4>{{ money(openDealsValue) }}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-8">
          <div class="bg-white rounded">
            <ul class="nav nav-tabs nav-tabs-bottom nav-justified flex-wrap mb-4">
              <li class="nav-item">
                <button type="button" :class="tabClass('overview')" @click="activeTab = 'overview'">
                  <i class="ti ti-layout-dashboard me-1"></i>Overview
                </button>
              </li>
              <li class="nav-item">
                <button type="button" :class="tabClass('contacts')" @click="activeTab = 'contacts'">
                  <i class="ti ti-users me-1"></i>Contacts
                </button>
              </li>
              <li class="nav-item">
                <button type="button" :class="tabClass('deals')" @click="activeTab = 'deals'">
                  <i class="ti ti-briefcase me-1"></i>Deals
                </button>
              </li>
              <li class="nav-item">
                <button type="button" :class="tabClass('projects')" @click="activeTab = 'projects'">
                  <i class="ti ti-box me-1"></i>Projects
                </button>
              </li>
              <li class="nav-item">
                <button type="button" :class="tabClass('invoices')" @click="activeTab = 'invoices'">
                  <i class="ti ti-file-invoice me-1"></i>Invoices
                </button>
              </li>
              <li class="nav-item">
                <button type="button" :class="tabClass('engagement')" @click="activeTab = 'engagement'">
                  <i class="ti ti-activity-heartbeat me-1"></i>Engagement
                </button>
              </li>
            </ul>
          </div>

          <div v-if="activeTab === 'overview'" class="card border-0">
            <div class="card-header">
              <h5 class="mb-0">Account Overview</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-6">
                  <div class="border rounded p-3 h-100">
                    <h6 class="fw-medium mb-2">Relationship Summary</h6>
                    <div class="small text-muted mb-2">This account currently has:</div>
                    <div class="d-flex flex-column gap-2">
                      <div>{{ contacts.length }} linked contacts</div>
                      <div>{{ deals.length }} linked deals</div>
                      <div>{{ projects.length }} linked projects</div>
                      <div>{{ invoices.length }} recent invoices</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="border rounded p-3 h-100">
                    <h6 class="fw-medium mb-2">Revenue Snapshot</h6>
                    <div class="small text-muted mb-2">Open pipeline value</div>
                    <h4 class="mb-2">{{ money(openDealsValue) }}</h4>
                    <div class="small text-muted">
                      Based on open deals currently linked to this account.
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="border rounded p-3">
                    <h6 class="fw-medium mb-3">Recent Linked Deals</h6>
                    <div v-if="!deals.length" class="text-muted">
                      No deals linked to this account yet.
                    </div>
                    <div v-else class="d-flex flex-column gap-3">
                      <div v-for="deal in deals.slice(0, 5)" :key="deal.id" class="border rounded p-3">
                        <div class="d-flex justify-content-between align-items-start gap-3">
                          <div>
                            <div class="fw-medium">{{ deal.name }}</div>
                            <div class="small text-muted">
                              {{ deal.pipeline?.name || "No pipeline" }}
                              <span v-if="deal.stage"> • {{ deal.stage.name }}</span>
                              <span v-if="deal.owner"> • {{ deal.owner.name }}</span>
                            </div>
                          </div>
                          <div class="text-end">
                            <div class="fw-medium">{{ money(deal.amount, deal.currency) }}</div>
                            <div class="small text-muted">{{ deal.status }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'contacts'" class="card border-0">
            <div class="card-header d-flex align-items-center justify-content-between">
              <h5 class="mb-0">Contacts</h5>
              <span class="badge bg-primary-transparent">{{ contacts.length }} records</span>
            </div>
            <div class="card-body">
              <div v-if="!contacts.length" class="border rounded p-3 text-muted">
                No contacts are linked to this account yet.
              </div>
              <div v-else class="d-flex flex-column gap-3">
                <div v-for="contact in contacts" :key="contact.id" class="border rounded p-3">
                  <div class="d-flex justify-content-between align-items-start gap-3">
                    <div>
                      <div class="fw-medium">{{ contact.name }}</div>
                      <div class="small text-muted">
                        {{ contact.job_title || "No job title" }}
                        <span v-if="contact.owner"> • {{ contact.owner.name }}</span>
                      </div>
                      <div class="small mt-2">
                        <span v-if="contact.email">{{ contact.email }}</span>
                        <span v-if="contact.email && contact.phone"> • </span>
                        <span v-if="contact.phone">{{ contact.phone }}</span>
                      </div>
                    </div>
                    <span :class="['badge', contact.status === 'Active' ? 'badge-soft-success' : 'badge-soft-danger']">
                      {{ contact.status }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'deals'" class="card border-0">
            <div class="card-header d-flex align-items-center justify-content-between">
              <h5 class="mb-0">Deals</h5>
              <span class="badge bg-primary-transparent">{{ deals.length }} records</span>
            </div>
            <div class="card-body">
              <div v-if="!deals.length" class="border rounded p-3 text-muted">
                No deals are linked to this account yet.
              </div>
              <div v-else class="d-flex flex-column gap-3">
                <div v-for="deal in deals" :key="deal.id" class="border rounded p-3">
                  <div class="d-flex justify-content-between align-items-start gap-3">
                    <div>
                      <div class="fw-medium">{{ deal.name }}</div>
                      <div class="small text-muted">
                        {{ deal.pipeline?.name || "No pipeline" }}
                        <span v-if="deal.stage"> • {{ deal.stage.name }}</span>
                        <span v-if="deal.owner"> • {{ deal.owner.name }}</span>
                      </div>
                      <div class="small mt-2 text-muted">
                        Expected close: {{ deal.expected_close_date || "—" }}
                      </div>
                    </div>
                    <div class="text-end">
                      <div class="fw-medium">{{ money(deal.amount, deal.currency) }}</div>
                      <div class="small text-muted">
                        {{ deal.status }}
                        <span v-if="deal.probability != null"> • {{ deal.probability }}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'projects'" class="card border-0">
            <div class="card-header d-flex align-items-center justify-content-between">
              <h5 class="mb-0">Projects</h5>
              <span class="badge bg-primary-transparent">{{ projects.length }} records</span>
            </div>
            <div class="card-body">
              <div v-if="!projects.length" class="border rounded p-3 text-muted">
                No projects are linked to this account yet.
              </div>
              <div v-else class="d-flex flex-column gap-3">
                <div v-for="project in projects" :key="project.id" class="border rounded p-3">
                  <div class="d-flex justify-content-between align-items-start gap-3">
                    <div>
                      <div class="fw-medium">{{ project.name }}</div>
                      <div class="small text-muted">
                        {{ project.status || "No status" }}
                        <span v-if="project.leader"> • {{ project.leader.name }}</span>
                      </div>
                      <div class="small mt-2 text-muted">
                        {{ project.tasks_completed }} / {{ project.tasks_total }} tasks completed
                      </div>
                    </div>
                    <div class="text-end">
                      <div class="fw-medium">{{ money(project.value) }}</div>
                      <div class="small text-muted">
                        {{ project.start_date || "—" }} to {{ project.end_date || "—" }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'invoices'" class="card border-0">
            <div class="card-header d-flex align-items-center justify-content-between">
              <h5 class="mb-0">Invoices</h5>
              <span class="badge bg-primary-transparent">{{ invoices.length }} records</span>
            </div>
            <div class="card-body">
              <div v-if="!invoices.length" class="border rounded p-3 text-muted">
                No invoices are linked to this account yet.
              </div>
              <div v-else class="d-flex flex-column gap-3">
                <div v-for="invoice in invoices" :key="invoice.id" class="border rounded p-3">
                  <div class="d-flex justify-content-between align-items-start gap-3">
                    <div>
                      <div class="fw-medium">{{ invoice.invoice_no || invoice.title || `Invoice #${invoice.id}` }}</div>
                      <div class="small text-muted">
                        Invoice date: {{ invoice.invoice_date || "—" }}
                        <span v-if="invoice.due_date"> • Due {{ invoice.due_date }}</span>
                      </div>
                    </div>
                    <div class="text-end">
                      <div class="fw-medium">{{ money(invoice.total) }}</div>
                      <div class="small text-muted">
                        {{ invoice.status }}
                        <span v-if="invoice.amount_due"> • Due {{ money(invoice.amount_due) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else>
            <crm-engagement-panel
              entity-type="client"
              :entity-id="clientId"
              :default-email="clientEmail || ''"
              note-placeholder="Add an internal note about this account"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import CrmEngagementPanel from "@/components/crm/CrmEngagementPanel.vue";
import { useCrmClientStore } from "@/stores/crmClientStore";
import { storeToRefs } from "pinia";

const route = useRoute();
const router = useRouter();
const store = useCrmClientStore();
const { client, loading, error } = storeToRefs(store);
const activeTab = ref("overview");

const clientId = computed(() => Number(route.params.id) || null);

async function loadClient() {
  if (!clientId.value) {
    router.replace({ name: "crm.clients.list" });
    return;
  }
  await store.fetchClient(clientId.value);
}

function reload() {
  loadClient();
}

onMounted(loadClient);

watch(
  () => route.params.id,
  async (val, oldVal) => {
    if (val && val !== oldVal) {
      activeTab.value = "overview";
      await loadClient();
    }
  }
);

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function tabClass(tab) {
  return [
    "nav-link fw-medium d-flex align-items-center justify-content-center",
    activeTab.value === tab ? "active" : "",
  ];
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function money(value, currency = "KES") {
  const amount = Number(value || 0);
  return `${currency} ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const clientName = computed(() => client.value?.name || "Account");
const clientEmail = computed(() => client.value?.email || "");
const clientPhone = computed(() => client.value?.phone || "");
const clientWebsite = computed(() => client.value?.website || "");
const clientAddress = computed(() => client.value?.address || "");
const clientCode = computed(
  () => client.value?.client_code || `CLT-${String(clientId.value || 0).padStart(4, "0")}`
);
const clientStatus = computed(() => client.value?.status || "Active");
const isActive = computed(() => clientStatus.value === "Active");
const clientLogo = computed(
  () => client.value?.logo_url || require("@/assets/img/users/user-13.jpg")
);
const clientCreatedAt = computed(() => client.value?.created_at || null);
const ownerName = computed(() => client.value?.user?.name || "Unassigned");
const ownerEmail = computed(() => client.value?.user?.email || "—");
const contacts = computed(() => client.value?.contacts || []);
const deals = computed(() => client.value?.deals || []);
const projects = computed(() => client.value?.projects || []);
const invoices = computed(() => client.value?.invoices || []);
const openDealsValue = computed(() => Number(client.value?.open_deals_value || 0));
</script>

<style scoped>
@media (max-width: 767.98px) {
  .dq-page-actions {
    align-items: flex-start !important;
    flex-direction: column;
    gap: 0.75rem;
  }

  .dq-page-actions > div:last-child {
    width: 100%;
  }

  .dq-page-actions .btn {
    width: 100%;
  }

  .nav-tabs {
    gap: 0.5rem;
  }

  .nav-tabs .nav-link {
    width: 100%;
  }

  .card-body.p-0 {
    overflow: hidden;
  }
}
</style>


